import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Calculator, Send, Loader2, PartyPopper } from "lucide-react";
import { API } from "@/constants/assets";

const BASE = { standard: 125, deep: 229, "move-in-out": 289, post: 349 };
const LABELS = { standard: "Residential Maintenance", deep: "Deep Cleaning", "move-in-out": "Move-In / Move-Out", post: "Post-Construction" };

const inputCls = "w-full bg-[#FAF7F2] border border-[#E5DEC9] rounded-xl px-4 py-3 text-[#1A1A1A] text-sm focus:outline-none focus:ring-2 focus:ring-[#990000] focus:border-transparent transition-all";

const QuoteForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_type: "standard", bedrooms: 3, bathrooms: 2, sqft: 1800, preferred_date: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const estimate = useMemo(() => {
    if (form.service_type === "standard") return 125;
    const base = BASE[form.service_type] || 125;
    const beds = (Number(form.bedrooms) - 1) * 22;
    const baths = (Number(form.bathrooms) - 1) * 18;
    const size = Math.max(0, Math.floor((Number(form.sqft) - 1200) / 500)) * 15;
    return Math.max(125, base + beds + baths + size);
  }, [form.service_type, form.bedrooms, form.bathrooms, form.sqft]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
        sqft: Number(form.sqft),
        estimated_price: estimate,
      });
      setDone(true);
      toast.success("Quote request sent! Lauren will reach out within one business day.");
    } catch (err) {
      toast.error("Something went wrong sending your quote. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" className="py-20 md:py-28 bg-[#F3EDE4] border-t border-[#E5DEC9]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_0.8fr] gap-10 lg:gap-16">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#990000] mb-3">Free instant quote</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug mb-8">
            Book your clean in <span className="italic text-[#990000]">60 seconds</span>
          </h2>
          {done ? (
            <div data-testid="booking-success-message" className="bg-white border border-[#E5DEC9] rounded-3xl p-10 text-center shadow-sm">
              <PartyPopper size={36} className="text-[#990000] mx-auto mb-4" />
              <h3 className="font-display text-2xl font-semibold">You're on the books!</h3>
              <p className="text-sm text-[#5C5955] mt-3 leading-relaxed">Thanks, {form.name.split(" ")[0] || "friend"} — your quote request landed in our bucket. Lauren will confirm your clean within one business day.</p>
              <button data-testid="booking-another-btn" onClick={() => setDone(false)} className="mt-6 bg-[#FDF0F0] text-[#990000] font-semibold py-2.5 px-6 rounded-full hover:bg-[#990000] hover:text-white transition-all duration-200 text-sm">
                Request another quote
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="booking-form">
              <input data-testid="booking-form-name-input" required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
              <input data-testid="booking-form-email-input" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
              <input data-testid="booking-form-phone-input" required placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
              <select data-testid="booking-form-service-type" value={form.service_type} onChange={set("service_type")} className={inputCls}>
                {Object.entries(LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <select data-testid="booking-form-bedrooms-select" value={form.bedrooms} onChange={set("bedrooms")} className={inputCls}>
                {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n} bedroom{n > 1 ? "s" : ""}</option>)}
              </select>
              <select data-testid="booking-form-bathrooms-select" value={form.bathrooms} onChange={set("bathrooms")} className={inputCls}>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} bathroom{n > 1 ? "s" : ""}</option>)}
              </select>
              <input data-testid="booking-form-sqft-input" required type="number" min="300" max="20000" step="50" placeholder="Home size (sq ft)" value={form.sqft} onChange={set("sqft")} className={inputCls} />
              <input data-testid="booking-form-date-input" type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} />
              <textarea data-testid="booking-form-notes-input" placeholder="Anything we should know? (pets, priorities, gate codes...)" value={form.notes} onChange={set("notes")} rows={3} className={`${inputCls} sm:col-span-2 resize-none`} />
              <button data-testid="booking-form-submit-btn" disabled={sending} type="submit" className="sm:col-span-2 inline-flex justify-center items-center gap-2 bg-[#990000] text-white font-semibold py-3.5 px-7 rounded-full shadow-md hover:bg-[#7A0000] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60 disabled:translate-y-0">
                {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
                {sending ? "Sending..." : "Request My Quote"}
              </button>
            </form>
          )}
        </motion.div>
        <motion.aside
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:sticky lg:top-28 self-start bg-[#1A1A1A] text-white rounded-3xl p-8 md:p-10 shadow-2xl"
          data-testid="quote-estimate-card"
        >
          <div className="flex items-center gap-2.5 text-[#D4AF37] text-xs font-bold uppercase tracking-[0.2em] mb-6">
            <Calculator size={15} /> Live estimate
          </div>
          <div className="font-display text-5xl md:text-6xl font-bold tracking-tight" data-testid="quote-estimated-price-display">
            ${estimate}<span className="text-xl text-white/50 font-medium">–${estimate + 40}</span>
          </div>
          <p className="text-sm text-white/60 mt-3 leading-relaxed">
            {LABELS[form.service_type]} · {form.bedrooms} bd / {form.bathrooms} ba · {Number(form.sqft).toLocaleString()} sq ft
          </p>
          <div className="border-t border-white/10 mt-7 pt-6 space-y-3.5 text-sm">
            {["No contracts, ever", "Supplies & equipment included", "100% satisfaction re-clean promise"].map((t, i) => (
              <div key={i} className="flex items-center gap-2.5 text-white/80">
                <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" /> {t}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-white/40 mt-7 leading-relaxed">Final pricing confirmed after a quick walkthrough. Estimates assume standard condition.</p>
        </motion.aside>
      </div>
    </section>
  );
};

export default QuoteForm;
